// Shared helpers for the Price History metrics + SEO summary.
// Works off the same feed the charts already receive:
//   [{ date, cpr, number_of_offers, vendor_name }, ...]

// Anything smaller than this (in %) is treated as "flat" rather than up/down.
const FLAT_THRESHOLD_PCT = 0.05;

/**
 * Compute summary metrics for a price-history series.
 * Returns null when there is nothing usable to summarize.
 */
export function computePriceMetrics(history) {
  const points = (Array.isArray(history) ? history : [])
    .map((item) => ({
      date: item?.date,
      cpr: Number(item?.cpr),
      offers: Number(item?.number_of_offers),
    }))
    .filter((item) => item.date && Number.isFinite(item.cpr));

  if (points.length === 0) return null;

  // Order oldest -> newest so "current" and trend direction are reliable,
  // regardless of the order the feed happens to return.
  const sorted = [...points].sort(
    (a, b) => new Date(a.date) - new Date(b.date),
  );

  const prices = sorted.map((p) => p.cpr);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const lowest = Math.min(...prices);
  const highest = Math.max(...prices);
  const average = prices.reduce((sum, p) => sum + p, 0) / prices.length;
  const current = last.cpr;
  const currentOffers = Number.isFinite(last.offers) ? last.offers : null;

  const changeAbs = current - first.cpr;
  const changePct = first.cpr !== 0 ? (changeAbs / first.cpr) * 100 : 0;
  const direction = trendDirection(changePct);

  // Trailing 3-month trend, independent of the selected range. The SEO
  // sentence always describes the "past 3 months" per spec, so with the
  // "Year" range selected we still measure change over the last 3 months.
  const threeMonthsAgo = new Date(last.date);
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const windowPoints = sorted.filter((p) => new Date(p.date) >= threeMonthsAgo);
  const windowFirst = windowPoints.length > 0 ? windowPoints[0] : first;
  const changeAbs3M = current - windowFirst.cpr;
  const changePct3M =
    windowFirst.cpr !== 0 ? (changeAbs3M / windowFirst.cpr) * 100 : 0;
  const direction3M = trendDirection(changePct3M);

  return {
    current,
    lowest,
    highest,
    average,
    currentOffers,
    changeAbs,
    changePct,
    direction,
    changeAbs3M,
    changePct3M,
    direction3M,
    dataPoints: sorted.length,
    firstDate: first.date,
    lastDate: last.date,
  };
}

/** Map a percentage change to "up" | "down" | "flat". */
function trendDirection(changePct) {
  if (changePct > FLAT_THRESHOLD_PCT) return "up";
  if (changePct < -FLAT_THRESHOLD_PCT) return "down";
  return "flat";
}

/** Human label for the selected period code. */
export function periodLabel(period) {
  switch (period) {
    case "1Y":
      return "past year";
    case "3M":
    default:
      return "past 3 months";
  }
}

/** "$0.450" — cost-per-round formatting used across the metrics UI. */
export function formatCpr(value, { withUnit = false } = {}) {
  if (!Number.isFinite(value)) return "N/A";
  const formatted = `$${value.toFixed(3)}`;
  return withUnit ? `${formatted}/rd` : formatted;
}

/** "+4.20%" / "-4.20%" — two decimals per the reference design. */
export function formatPct(value) {
  if (!Number.isFinite(value)) return "N/A";
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  return `${sign}${Math.abs(value).toFixed(2)}%`;
}

/**
 * Turn a product-detail URL slug into a readable name for the summary text.
 * "federal-9mm-luger-115gr-fmj-123" -> "Federal 9mm Luger 115gr Fmj"
 */
export function prettifyProductSlug(slug) {
  if (!slug) return "";
  return slug
    .replace(/-\d+$/, "") // drop the trailing "-<productId>"
    .split("-")
    .filter(Boolean)
    .map((word) => (/\d/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ")
    .trim();
}

/** "Jul 20, 2026" for the "As of ..." summary sentence. */
export function formatAsOfDate(date = new Date()) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

/**
 * SEO-friendly text version of the data, e.g.:
 * "As of Jul 20, 2026, 9mm Luger is trending down 4.20% in price over the
 *  past 3 months, with a current lowest price of $0.450/rd, versus a
 *  past-3-months average of $0.480/rd."
 */
export function buildPriceSummarySentence({ label, period, metrics, asOf }) {
  if (!metrics) return "";

  const name = label && label.trim() ? label.trim() : "This item";
  const range = periodLabel(period);
  const asOfDate = formatAsOfDate(asOf);

  // Trend is always measured over the trailing 3 months (per spec), regardless
  // of the range toggle; the average reflects the selected period.
  const trend =
    metrics.direction3M === "flat"
      ? "holding roughly flat"
      : `trending ${metrics.direction3M} ${Math.abs(
          metrics.changePct3M,
        ).toFixed(2)}%`;

  return (
    `As of ${asOfDate}, ${name} is ${trend} in price over the past 3 months, ` +
    `with a current lowest price of ${formatCpr(metrics.current, {
      withUnit: true,
    })}, versus a ${range.replace(/\s+/g, "-")} average of ${formatCpr(
      metrics.average,
      { withUnit: true },
    )}.`
  );
}
